import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

export default function TextHighlighter({ html, children, docId = 'default' }) {
  const [highlights, setHighlights] = useState([]); // {id, start, end, note, highlighted}
  const [menu, setMenu] = useState({ visible: false, x: 0, y: 0, inside: null });
  const menuRef = useRef(null);
  const [popup, setPopup] = useState({ visible: false, id: null, text: '' });
  const [markers, setMarkers] = useState([]);
  const containerRef = useRef(null);
  const rangeRef = useRef(null);
  const popupRef = useRef(null);

  // Load highlights from sessionStorage on mount - use unique key per docId
  useEffect(() => {
    const storageKey = `highlights-${docId}`;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setHighlights(Array.isArray(parsed) ? parsed : Object.values(parsed));
      } catch (e) {
        console.error('Failed to parse saved highlights', e);
      }
    }
  }, [docId]);

  // Save highlights to sessionStorage whenever highlights change - use unique key per docId
  useEffect(() => {
    const storageKey = `highlights-${docId}`;
    sessionStorage.setItem(storageKey, JSON.stringify(highlights));
  }, [highlights, docId]);

  // Apply highlights after initial mount and whenever highlights or html/children change
  useLayoutEffect(() => {
    renderHighlights();
    const handle = () => updateMarkers();
    window.addEventListener('resize', handle);
    const ctr = containerRef.current;
    ctr && ctr.addEventListener('scroll', handle);
    handle();
    return () => {
      window.removeEventListener('resize', handle);
      ctr && ctr.removeEventListener('scroll', handle);
    };
  }, [highlights, html, children]);

  const removeExistingMarks = () => {
    if (!containerRef.current) return;
    containerRef.current.querySelectorAll('mark').forEach(m => {
      const p = m.parentNode;
      while (m.firstChild) p.insertBefore(m.firstChild, m);
      p.removeChild(m);
      // Only normalize if we're not dealing with React components
      if (!m.closest('[data-block-id]')) {
        p.normalize();
      }
    });
  };

  const getCharOffset = (root, node, offset) => {
    let idx = 0;
    const walker = document.createTreeWalker(
      root, 
      NodeFilter.SHOW_TEXT, 
      {
        acceptNode: (node) => {
          // Only include text nodes within the current container
          return containerRef.current && containerRef.current.contains(node) 
            ? NodeFilter.FILTER_ACCEPT 
            : NodeFilter.FILTER_REJECT;
        }
      }
    );
    let cur;
    while ((cur = walker.nextNode())) {
      if (cur === node) return idx + offset;
      idx += cur.textContent.length;
    }
    return idx;
  };

  const createSegments = (root, start, end) => {
    const segments = [];
    let idx = 0;
    const walker = document.createTreeWalker(
      root, 
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Only include text nodes within the current container
          // and exclude nodes within React component blocks
          if (!containerRef.current || !containerRef.current.contains(node)) {
            return NodeFilter.FILTER_REJECT;
          }
          
          // Skip text nodes within React component blocks
          if (node.closest && node.closest('[data-block-id]')) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const len = node.textContent.length;
      const nodeStart = idx;
      const nodeEnd = idx + len;
      if (nodeEnd <= start) {
        idx = nodeEnd;
        continue;
      }
      if (nodeStart >= end) break;
      const segStart = Math.max(start - nodeStart, 0);
      const segEnd = Math.min(end - nodeStart, len);
      if (segStart < segEnd) segments.push({ node, segStart, segEnd });
      idx = nodeEnd;
    }
    return segments;
  };

  const applyHighlight = h => {
    const segments = createSegments(containerRef.current, h.start, h.end);
    segments.forEach(({ node, segStart, segEnd }) => {
      // Skip if the node is within a React component block
      if (node.closest && node.closest('[data-block-id]')) {
        return;
      }
      
      const range = document.createRange();
      range.setStart(node, segStart);
      range.setEnd(node, segEnd);
      const mark = document.createElement('mark');
      mark.dataset.id = h.id;
      mark.classList.add('text-highlight');
      if (h.note) mark.classList.add('has-note');
      if (h.highlighted) {
        mark.style.backgroundColor = 'var(--highlight-color, #ffeb3b)';
        mark.style.color = 'var(--highlight-text-color, #000)';
      } else {
        mark.style.backgroundColor = 'transparent';
        mark.style.color = 'inherit';
      }
      range.surroundContents(mark);
    });
  };

  const updateMarkers = () => {
    const ctr = containerRef.current;
    if (!ctr) return;
    const base = ctr.getBoundingClientRect();
    const posMap = new Map();
    ctr.querySelectorAll('mark.has-note').forEach(m => {
      const rect = m.getBoundingClientRect();
      const existing = posMap.get(m.dataset.id);
      if (existing) {
        existing.top = Math.min(existing.top, rect.top);
        existing.left = Math.min(existing.left, rect.left);
        existing.right = Math.max(existing.right, rect.right);
      } else {
        posMap.set(m.dataset.id, {
          top: rect.top,
          left: rect.left,
          right: rect.right,
        });
      }
    });

    const counts = {};
    const newMarkers = [];
    posMap.forEach((pos, id) => {
      const key = `${pos.top}-${pos.left}`;
      const offset = counts[key] || 0;
      counts[key] = offset + 1;
      const center = (pos.left + pos.right) / 2;
      newMarkers.push({
        id,
        top: pos.top - base.top - 20,
        left: center - base.left - 8 + offset * 12,
      });
    });
    setMarkers(newMarkers);
  };

  const renderHighlights = () => {
    removeExistingMarks();
    highlights.forEach(applyHighlight);
    // Only normalize if we're not dealing with React components
    if (containerRef.current && !containerRef.current.querySelector('[data-block-id]')) {
      containerRef.current.normalize();
    }
    updateMarkers();
  };

  const showMenuForSelection = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    
    // Check if selection is within our container
    if (!containerRef.current || 
        !containerRef.current.contains(range.commonAncestorContainer)) {
      return;
    }
    
    rangeRef.current = range.cloneRange();
    const rect = range.getBoundingClientRect();
    const startMark = range.startContainer.parentElement?.closest('mark');
    const endMark = range.endContainer.parentElement?.closest('mark');
    const inside = startMark && startMark === endMark ? startMark : null;
    
    // Better positioning for context menu
    const menuX = Math.max(10, Math.min(window.innerWidth - 200, rect.left));
    const menuY = Math.max(10, rect.top - 40);
    
    setMenu({
      visible: true,
      x: menuX,
      y: menuY,
      inside,
    });
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handler = e => {
      if (
        menu.visible &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setMenu(m => ({ ...m, visible: false }));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menu.visible]);

  const addHighlight = () => {
    const range = rangeRef.current;
    if (!range || range.collapsed) return;
    const start = getCharOffset(containerRef.current, range.startContainer, range.startOffset);
    const end = getCharOffset(containerRef.current, range.endContainer, range.endOffset);
    const id = uuidv4();
    setHighlights(hs => [...hs, { id, start, end, note: '', highlighted: true }]);
    setMenu(m => ({ ...m, visible: false }));
  };

  const addNote = () => {
    const range = rangeRef.current;
    if (!range || range.collapsed) return;
    const start = getCharOffset(containerRef.current, range.startContainer, range.startOffset);
    const end = getCharOffset(containerRef.current, range.endContainer, range.endOffset);
    const id = uuidv4();
    setHighlights(hs => [...hs, { id, start, end, note: '', highlighted: false }]);
    setPopup({ visible: true, id, text: '' });
    setMenu(m => ({ ...m, visible: false }));
  };

  const removeHighlight = () => {
    const range = rangeRef.current;
    if (!range) return;
    const selStart = getCharOffset(containerRef.current, range.startContainer, range.startOffset);
    const selEnd = getCharOffset(containerRef.current, range.endContainer, range.endOffset);
    
    setHighlights(hs => 
      hs.filter(h => {
        // Remove highlights that are completely within the selection
        if (h.highlighted && selStart <= h.start && selEnd >= h.end) {
          return false;
        }
        // For partial overlaps, split the highlight
        if (h.highlighted && !(selEnd <= h.start || selStart >= h.end)) {
          // This will be handled by the split logic below
          return true;
        }
        return true;
      }).flatMap(h => {
        // Split highlights that are partially overlapped
        if (h.highlighted && !(selEnd <= h.start || selStart >= h.end)) {
          const result = [];
          if (h.start < selStart) {
            result.push({ ...h, end: selStart });
          }
          if (h.end > selEnd) {
            result.push({ ...h, start: selEnd });
          }
          return result.filter(r => r.end > r.start);
        }
        return [h];
      })
    );
    setMenu(m => ({ ...m, visible: false }));
  };

  const openNotePopup = rangeOrId => {
    let id;
    if (typeof rangeOrId === 'string') id = rangeOrId;
    else {
      const mark = rangeOrId.commonAncestorContainer.parentElement.closest('mark');
      id = mark?.dataset.id;
    }
    if (!id) return;
    const found = highlights.find(h => h.id === id) || { note: '' };
    setPopup({ visible: true, id, text: found.note || '' });
    setMenu(m => ({ ...m, visible: false }));
  };

  const onNoteChange = e => {
    const text = e.target.value;
    setHighlights(hs => hs.map(h => (h.id === popup.id ? { ...h, note: text } : h)));
    setPopup(p => ({ ...p, text }));
  };

  const deleteNote = () => {
    setHighlights(hs =>
      hs.filter(h => {
        if (h.id !== popup.id) return true;
        // Keep the highlight if it has highlighting, remove if it's just a note
        return h.highlighted;
      }).map(h => 
        h.id === popup.id ? { ...h, note: '' } : h
      )
    );
    setPopup(p => ({ ...p, visible: false }));
  };

  // Close popup when clicking outside
  useEffect(() => {
    if (!popup.visible) return;
    const handler = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setPopup(p => ({ ...p, visible: false }));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popup.visible]);

  const menuItems = [
    { 
      label: 'Highlight', 
      icon: '🖍️',
      onClick: addHighlight, 
      title: 'Highlight selected text' 
    },
    {
      label: 'Remove',
      icon: '🗑️',
      onClick: removeHighlight,
      title: 'Remove highlight from selected text',
    },
    {
      label: 'Note',
      icon: '📝',
      onClick: () =>
        menu.inside ? openNotePopup(menu.inside.dataset.id) : addNote(),
      title: 'Add or edit note',
    },
  ];

  return (
    <div
      style={{ position: 'relative', margin: 0, padding: 0 }}
      onMouseUp={showMenuForSelection}
    >
      {html ? (
        <div ref={containerRef} style={{ margin: 0, padding: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div ref={containerRef} style={{ margin: 0, padding: 0 }}>{children}</div>
      )}

      {/* Note markers */}
      {markers.map(m => (
        <span
          key={m.id}
          onClick={() => openNotePopup(m.id)}
          className="note-marker"
          style={{ 
            position: 'absolute', 
            top: m.top, 
            left: m.left,
            backgroundColor: 'var(--background-color)',
            color: 'var(--text-color)',
            border: '1px solid var(--border-color)',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            cursor: 'pointer',
            zIndex: 100,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📝
        </span>
      ))}

      {/* Context menu */}
      {menu.visible && (
        <div
          className="highlight-menu"
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menu.y,
            left: menu.x,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--background-color)',
            color: 'var(--text-color)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            padding: '4px 0',
            borderRadius: '6px',
            zIndex: 10000,
            minWidth: '120px',
          }}
        >
          {menuItems.map((item, i) => (
            <button
              key={i}
              title={item.title}
              onClick={item.onClick}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 12px',
                fontSize: '14px',
                color: 'var(--text-color)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                textAlign: 'left',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(128, 128, 128, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Note popup */}
      {popup.visible && (
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--background-color)',
            color: 'var(--text-color)',
            border: '2px solid var(--border-color)',
            padding: '16px',
            zIndex: 11000,
            width: '400px',
            maxWidth: '90vw',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            borderRadius: '8px'
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Add Note</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={deleteNote}
                style={{ 
                  border: 'none', 
                  background: 'transparent', 
                  fontSize: '16px', 
                  cursor: 'pointer',
                  color: 'var(--text-color)',
                  padding: '4px'
                }}
                title="Delete note"
              >
                🗑️
              </button>
              <button
                onClick={() => setPopup(p => ({ ...p, visible: false }))}
                style={{ 
                  border: 'none', 
                  background: 'transparent', 
                  fontSize: '16px', 
                  cursor: 'pointer',
                  color: 'var(--text-color)',
                  padding: '4px'
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
          <textarea
            value={popup.text}
            onChange={onNoteChange}
            placeholder="Enter your note here..."
            style={{ 
              width: '100%', 
              height: '100px', 
              background: 'var(--background-color)', 
              color: 'var(--text-color)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              padding: '8px',
              resize: 'vertical',
              fontSize: '14px',
              fontFamily: 'inherit'
            }}
            autoFocus
          />
        </div>
      )}
    </div>
  );
}